import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form, Button, Container, Row, Col, Card, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { registrationAPI } from '../services/api';
import HostedCheckoutForm from './HostedCheckoutForm';
import PaynimoCheckout from './PaynimoCheckout';
import PaymentSuccess from './PaymentSuccess';

const RegistrationForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [calculatedFee, setCalculatedFee] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [registrationData, setRegistrationData] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentGateway, setPaymentGateway] = useState('paynimo'); // 'worldline' or 'paynimo'
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      supervisor_recommendation: false,
      participant_type: 'Student'
    }
  });

  const watchParticipantType = watch('participant_type');
  const watchDesignation = watch('designation');

  // Calculate fee when participant type changes
  useEffect(() => {
    const calculateFee = async () => {
      if (watchParticipantType) {
        try {
          const response = await registrationAPI.getFeeCalculation(watchParticipantType);
          setCalculatedFee(response.data.fee);
          setFeeInfo(response.data);
        } catch (error) {
          console.error('Fee calculation error:', error);
          toast.error('Failed to calculate fee');
        }
      }
    };

    calculateFee();
  }, [watchParticipantType]);

  // Auto-determine participant type based on designation
  useEffect(() => {
    if (watchDesignation) {
      const designation = watchDesignation.toLowerCase();
      if (designation.includes('student') || designation.includes('research scholar') || 
          designation.includes('phd') || designation.includes('m.sc') || 
          designation.includes('b.sc') || designation.includes('mtech')) {
        setValue('participant_type', 'Student');
      } else if (designation.includes('professor') || designation.includes('faculty') || 
                 designation.includes('lecturer') || designation.includes('assistant professor') ||
                 designation.includes('associate professor') || designation.includes('dr.')) {
        setValue('participant_type', 'Faculty');
      }
    }
  }, [watchDesignation, setValue]);


  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      // Use provided email and phone
      let email = data.email;
      let phone = data.phone;

      // Format the data
      const formattedData = {
        ...data,
        supervisor_recommendation: data.supervisor_recommendation === 'true' || data.supervisor_recommendation === true,
        email,
        phone
      };

      const response = await registrationAPI.registerParticipant(formattedData);
      
      setRegistrationData(response.data);
      setSubmitSuccess(true);
      setShowPayment(true);
      toast.success('Registration successful! Please proceed with payment.');

    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNewRegistration = () => {
    setSubmitSuccess(false);
    setRegistrationData(null);
    setShowPayment(false);
    setPaymentSuccess(false);
    setPaymentData(null);
    reset();
    setCalculatedFee(null);
    setFeeInfo(null);
  };

  const handlePaymentSuccess = (paymentResult) => {
    setPaymentData(paymentResult);
    setPaymentSuccess(true);
    setShowPayment(false);
  };

  const handlePaymentCancel = () => {
    setShowPayment(false);
    setSubmitSuccess(false);
    // Keep registration data but allow user to try payment again
    toast.info('Payment cancelled. You can try again later.');
  };

  // Show payment success page
  if (paymentSuccess && registrationData && paymentData) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={10}>
            <PaymentSuccess 
              registrationData={registrationData}
              paymentData={paymentData}
              onNewRegistration={handleNewRegistration}
            />
          </Col>
        </Row>
      </Container>
    );
  }

  // Show payment form
  if (showPayment && registrationData) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            {paymentGateway === 'paynimo' ? (
              <PaynimoCheckout 
                registrationData={registrationData}
                onPaymentSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            ) : (
              <HostedCheckoutForm 
                registrationData={registrationData}
                onPaymentSuccess={handlePaymentSuccess}
                onCancel={handlePaymentCancel}
              />
            )}
          </Col>
        </Row>
      </Container>
    );
  }

  // Show registration success without payment (legacy)
  if (submitSuccess && registrationData && !showPayment) {
    return (
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow">
              <Card.Header className="bg-success text-white text-center">
                <h3>Registration Successful!</h3>
              </Card.Header>
              <Card.Body className="text-center">
                <div className="mb-4">
                  <h4>Thank you, {registrationData.name}!</h4>
                  <p className="text-muted">Your registration has been confirmed.</p>
                </div>
                
                <Row>
                  <Col md={6}>
                    <strong>Registration ID:</strong>
                    <p>{registrationData.id}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Registration Fee:</strong>
                    <p className="text-success">₹{registrationData.fee}</p>
                  </Col>
                </Row>
                
                <Row>
                  <Col md={6}>
                    <strong>Email:</strong>
                    <p>{registrationData.email}</p>
                  </Col>
                  <Col md={6}>
                    <strong>Registered On:</strong>
                    <p>{new Date(registrationData.registered_at).toLocaleDateString()}</p>
                  </Col>
                </Row>

                <Alert variant="info" className="mt-4">
                  <strong>Next Steps:</strong>
                  <ul className="text-start mt-2">
                    <li>Please save your Registration ID: <strong>{registrationData.id}</strong></li>
                    <li>Payment instructions will be sent to your email</li>
                    <li>Keep this confirmation for your records</li>
                  </ul>
                </Alert>

                <Button 
                  variant="primary" 
                  onClick={handleNewRegistration}
                  className="mt-3"
                >
                  Register Another Participant
                </Button>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col lg={10} xl={9}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-primary text-white text-center py-3">
              <h3 className="d-block mb-1">School of Physical Sciences</h3>
              <h5 className="mb-1">NSDSST 2025 Registration</h5>
              <small>National Symposium on Dielectric Spectroscopy in Science and Technology</small>
              <div className="mt-2">
                <small><i className="fas fa-calendar-alt me-2"></i>October 31st, 2025</small>
                <span className="mx-2">|</span>
                <small><i className="fas fa-map-marker-alt me-2"></i>S.R.T.M. University, Nanded</small>
              </div>
            </Card.Header>
            <Card.Body className="p-4">
                  {feeInfo && (
                    <Alert variant={feeInfo.is_early_registration ? 'success' : (feeInfo.is_late_registration ? 'warning' : 'danger')} className="mb-3">
                      <div className="d-flex justify-content-between text-start">
                        <div>
                          <strong>Registration Fee: ₹{calculatedFee}</strong>
                          <br />
                          <small>
                            {feeInfo.is_early_registration 
                              ? `Early registration (until ${feeInfo.early_cutoff_date})`
                              : (feeInfo.is_late_registration 
                                ? `Late registration (${feeInfo.early_cutoff_date} to ${feeInfo.late_cutoff_date})`
                                : `Registration closed`
                              )
                            }
                          </small>
                        </div>
                        <div className="text-end">
                          <small>Last date: {feeInfo.last_date}</small>
                        </div>
                      </div>
                      <div className="mt-2 small">
                        <div className="row">
                          <div className="col-md-6 text-start">
                            <strong>Fee Structure:</strong>
                            <ul className="ps-3 mb-0">
                              <li>Research Scholar: ₹300 (early) / ₹500 (late)</li>
                              <li>Faculty: ₹500 (early) / ₹700 (late)</li>
                            </ul>
                          </div>
                          <div className="col-md-6 text-start">
                            <strong>Registration Periods:</strong>
                            <ul className="ps-3 mb-0">
                              <li>Early: Until 30th September 2025</li>
                              <li>Late: 1st October - 15th October 2025</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit(onSubmit)}>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Full Name *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter your full name"
                            {...register('name', { 
                              required: 'Name is required',
                              minLength: { value: 2, message: 'Name must be at least 2 characters' }
                            })}
                            isInvalid={!!errors.name}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Gender *</Form.Label>
                          <div className="mt-2">
                            <Form.Check
                              inline
                              type="radio"
                              label="Male"
                              value="Male"
                              {...register('sex', { required: 'Please select gender' })}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              label="Female"
                              value="Female"
                              {...register('sex', { required: 'Please select gender' })}
                            />
                          </div>
                          {errors.sex && (
                            <div className="text-danger small">{errors.sex.message}</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Designation *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Research Scholar, Assistant Professor"
                            {...register('designation', { 
                              required: 'Designation is required',
                              minLength: { value: 2, message: 'Designation must be at least 2 characters' }
                            })}
                            isInvalid={!!errors.designation}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.designation?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Participant Type *</Form.Label>
                          <Form.Select
                            {...register('participant_type', { required: 'Please select participant type' })}
                            isInvalid={!!errors.participant_type}
                          >
                            <option value="Student">Student</option>
                            <option value="Faculty">Faculty</option>
                          </Form.Select>
                          <Form.Control.Feedback type="invalid">
                            {errors.participant_type?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>College/Institute *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="Enter your college or institute"
                            {...register('college', { 
                              required: 'College/Institute name is required',
                              minLength: { value: 2, message: 'College name must be at least 2 characters' }
                            })}
                            isInvalid={!!errors.college}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.college?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email Address *</Form.Label>
                          <Form.Control
                            type="email"
                            placeholder="Enter your email address"
                            {...register('email', { 
                              required: 'Email is required',
                              pattern: {
                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                message: 'Please enter a valid email address'
                              }
                            })}
                            isInvalid={!!errors.email}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Phone Number *</Form.Label>
                          <Form.Control
                            type="tel"
                            placeholder="Enter your phone number"
                            {...register('phone', { 
                              required: 'Phone number is required',
                              pattern: {
                                value: /^[0-9+\-\s()]{10,15}$/,
                                message: 'Please enter a valid phone number'
                              }
                            })}
                            isInvalid={!!errors.phone}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.phone?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Subject *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Physics, Chemistry"
                            {...register('subject', { 
                              required: 'Subject is required',
                              minLength: { value: 2, message: 'Subject must be at least 2 characters' }
                            })}
                            isInvalid={!!errors.subject}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.subject?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Research Area *</Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g., Dielectric Spectroscopy"
                            {...register('research_area', { 
                              required: 'Research area is required',
                              minLength: { value: 2, message: 'Research area must be at least 2 characters' }
                            })}
                            isInvalid={!!errors.research_area}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.research_area?.message}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Supervisor Recommendation *</Form.Label>
                          <div className="mt-2">
                            <Form.Check
                              inline
                              type="radio"
                              label="Yes"
                              value={true}
                              {...register('supervisor_recommendation', { required: 'Please select an option' })}
                            />
                            <Form.Check
                              inline
                              type="radio"
                              label="No"
                              value={false}
                              {...register('supervisor_recommendation', { required: 'Please select an option' })}
                            />
                          </div>
                          {errors.supervisor_recommendation && (
                            <div className="text-danger small">{errors.supervisor_recommendation.message}</div>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-4">
                      <Form.Label>Address for Correspondence *</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Complete address including city, state, and postal code"
                        {...register('address', { 
                          required: 'Address is required',
                          minLength: { value: 10, message: 'Please provide a complete address' }
                        })}
                        isInvalid={!!errors.address}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.address?.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                <div className="text-center">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={isSubmitting}
                    className="px-5"
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      `Submit`
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default RegistrationForm;
